import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';

/*
 * DocxHighlightViewer: the Word counterpart of PdfHighlightViewer.
 *
 * DocumentPreview only highlights PDFs. A .docx goes to DocViewer, a plain Word viewer that can neither
 * highlight nor scroll. This renders the .docx in the page, finds the cited passage the same way
 * PdfHighlightViewer does (text1 -> text2 -> text3, exact match, then sliding probes, then a distinctive
 * word), highlights it and scrolls to it.
 *
 * Props (same names as PdfHighlightViewer):
 *   docUrl    link to the .docx
 *   text1     highest priority text (table caption)
 *   text2     verbatim phrase
 *   text3     whole chunk
 *   fallback  what to show if the file cannot be fetched or rendered (e.g. the existing <DocViewer />)
 */

// =====================================================================
//  MARKDOWN STRIPPING  (same as PdfHighlightViewer)
// =====================================================================

const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[=\-]{2,}\s*$/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, ' $1 ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
    .replace(/(\*{3}|_{3})(.*?)\1/g, '$2')
    .replace(/(\*{2}|_{2})(.*?)\1/g, '$2')
    .replace(/([*_])(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    .replace(/^[-*_]{3,}\s*$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/^[\s:|-]+$/gm, '')
    .replace(/^\[[^\]]+\]:\s*\S+.*$/gm, '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

// A stored chunk is indexer output, not text from the file: a table chunk starts with a generated
// summary ("This table contains ... with the following columns: ...") and carries pandas leftovers
// (|Unnamed: 2|, nan, |---|). None of that is in the .docx, and a long summary pushes the real text
// past the point where the sliding probes stop looking. Keep only what the file contains.
const cleanChunk = (content) => {
  if (!content || typeof content !== 'string') return content;
  let text = content.replace(/\r/g, '');

  const firstRow = text.search(/^\s*\|/m);
  if (firstRow > 0) {
    const prefix = text.slice(0, firstRow);
    if (/^\s*(this (is|table)|the (table|following))/i.test(prefix) || /following columns/i.test(prefix)) {
      text = text.slice(firstRow);
    }
  }

  const lines = text.split('\n').map((line) => {
    if (!line.includes('|')) return line;
    return line
      .split('|')
      .map((c) => c.trim())
      .filter((c) => c && c.toLowerCase() !== 'nan' && !/^Unnamed:\s*\d+$/i.test(c) && !/^:?-{3,}:?$/.test(c))
      .join(' ');
  });

  const cleaned = lines.join('\n');
  return cleaned.replace(/\s+/g, ' ').trim().length >= 12 ? cleaned : content;
};

// =====================================================================
//  TEXT FOLDING  (one rule for the document and for the search text)
// =====================================================================

const DROP_RE = /[\u00AD\u200B\u200C\u200D\uFEFF]/;
const FOLD = {
  '\u2018': "'", '\u2019': "'", '\u201A': "'", '\u201B': "'", '`': "'",
  '\u201C': '"', '\u201D': '"', '\u201E': '"', '\u201F': '"',
  '\u2013': '-', '\u2014': '-', '\u2015': '-', '\u2212': '-',
  '\u2026': '...',
  '\uFB01': 'fi', '\uFB02': 'fl', '\uFB00': 'ff', '\uFB03': 'ffi', '\uFB04': 'ffl',
};

const foldChar = (ch) => {
  if (DROP_RE.test(ch)) return '';
  if (FOLD[ch] !== undefined) return FOLD[ch];
  if (/[*_~#>|\\]/.test(ch)) return ' ';
  if (/\s/.test(ch)) return ' ';
  return ch.toLowerCase();
};

const foldString = (s) => {
  let out = '';
  for (const ch of s || '') {
    for (const c of foldChar(ch)) {
      if (c === ' ') {
        if (out && out[out.length - 1] !== ' ') out += ' ';
      } else {
        out += c;
      }
    }
  }
  return out.trim();
};

// =====================================================================
//  DOCUMENT TEXT INDEX
// =====================================================================

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'TD', 'TH', 'TR', 'LI', 'UL', 'OL', 'TABLE', 'TBODY', 'THEAD', 'TFOOT',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'CAPTION', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'BLOCKQUOTE',
]);

/**
 * The rendered document as one searchable string, with a map from every character back to the text node
 * (and offset) it came from.
 *
 * Word stores one sentence as many runs, often splitting a word in two ("Marktverfüg" + "barkeit"), so
 * text inside one paragraph is joined WITHOUT a separator. A space goes in only where a block ends (a
 * paragraph, a table cell, a row) or at a line break, which is where a reader sees a gap.
 */
const buildTextIndex = (root) => {
  let concat = '';
  const map = []; // map[i] = { node, offset } | null (a separator that belongs to no text node)
  let needSpace = false;

  const push = (c, node, offset) => {
    concat += c;
    map.push(node ? { node, offset } : null);
  };

  const walk = (el) => {
    Array.from(el.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        const text = child.nodeValue || '';
        for (let i = 0; i < text.length; i++) {
          for (const c of foldChar(text[i])) {
            if (c === ' ') {
              if (concat && concat[concat.length - 1] !== ' ') push(' ', child, i);
              needSpace = false;
            } else {
              if (needSpace && concat && concat[concat.length - 1] !== ' ') push(' ', null, 0);
              needSpace = false;
              push(c, child, i);
            }
          }
        }
      } else if (child.nodeType === 1) {
        const tag = child.tagName;
        if (tag === 'STYLE' || tag === 'SCRIPT') return;
        if (tag === 'BR') { needSpace = true; return; }
        const block = BLOCK_TAGS.has(tag);
        if (block) needSpace = true;
        walk(child);
        if (block) needSpace = true;
      }
    });
  };

  walk(root);
  return { concat, map };
};

// =====================================================================
//  MATCHING  (the same steps PdfHighlightViewer uses)
// =====================================================================

const STOP_WORDS = new Set([
  'a','an','the','of','in','to','for','and','or','is','are','was',
  'were','be','been','being','with','by','at','on','from','as','it',
  'its','this','that','these','those','we','they','he','she','which',
  'who','whom','but','not','no','all','each','has','have','had',
  'do','does','did','will','would','could','should','may','might',
  'can','than','also','into','about','such','between','through',
]);

const probesAt = (words, startIdx) => {
  const probes = [];
  for (const size of [12, 8, 6, 4, 3]) {
    const end = Math.min(startIdx + size, words.length);
    if (end - startIdx < Math.min(size, 3)) continue;
    probes.push(words.slice(startIdx, end).join(' '));
  }
  return probes;
};

const findMatch = (concat, rawText) => {
  const words = foldString(stripMarkdown(rawText)).split(' ').filter(Boolean);
  if (words.length === 0 || !concat) return null;

  let start = -1;
  let end = -1;

  // 1. direct substring: the first and the last 20 words
  const head = words.slice(0, Math.min(20, words.length)).join(' ');
  start = concat.indexOf(head);
  if (start !== -1) {
    const tail = words.slice(Math.max(0, words.length - 20)).join(' ');
    const t = concat.indexOf(tail, start);
    end = t !== -1 ? t + tail.length : start + head.length;
  }

  // 2. sliding probes, forward for the start and backward for the end
  if (start === -1) {
    outer: for (let pos = 0; pos < Math.min(words.length, 4 * 40); pos += 4) {
      for (const probe of probesAt(words, pos)) {
        const idx = concat.indexOf(probe);
        if (idx !== -1) { start = idx; break outer; }
      }
    }
  }
  if (start !== -1 && end === -1) {
    outer2: for (let off = 0; off < Math.min(words.length, 4 * 40); off += 4) {
      const s = Math.max(0, words.length - 12 - off);
      for (const probe of probesAt(words, s)) {
        const idx = concat.indexOf(probe, start);
        if (idx !== -1) { end = idx + probe.length; break outer2; }
      }
    }
    if (end === -1 || end <= start) end = start + Math.min(words.join(' ').length, concat.length - start);
  }

  // 3. a distinctive word, with a 200 character window
  if (start === -1) {
    const distinctive = words.filter((w) => !STOP_WORDS.has(w) && w.length > 4).sort((a, b) => b.length - a.length).slice(0, 5);
    for (const w of distinctive) {
      const idx = concat.indexOf(w);
      if (idx !== -1) { start = idx; end = Math.min(idx + 200, concat.length); break; }
    }
  }

  if (start === -1) return null;
  return { start, end: Math.min(end, concat.length) };
};

// =====================================================================
//  HIGHLIGHT + SCROLL
// =====================================================================

const MARK_CLASS = 'docx-source-highlight';

/** Highlights the first text in `texts` that can be found. Returns { found, marks, matchedIndex }. */
const highlightPassage = (root, texts) => {
  const doc = root.ownerDocument;
  const { concat, map } = buildTextIndex(root);

  for (let ti = 0; ti < texts.length; ti++) {
    if (!texts[ti]) continue;
    const m = findMatch(concat, texts[ti]);
    if (!m) continue;

    let s = m.start;
    while (s < m.end && !map[s]) s++;
    let e = m.end - 1;
    while (e > s && !map[e]) e--;
    if (s > e || !map[s]) continue;

    const spans = new Map(); // text node -> [first offset, last offset]
    for (let i = s; i <= e; i++) {
      const hit = map[i];
      if (!hit) continue;
      const cur = spans.get(hit.node);
      if (!cur) spans.set(hit.node, [hit.offset, hit.offset]);
      else { cur[0] = Math.min(cur[0], hit.offset); cur[1] = Math.max(cur[1], hit.offset); }
    }

    const marks = [];
    spans.forEach(([a, b], node) => {
      if (!node.parentNode) return;
      const range = doc.createRange();
      range.setStart(node, a);
      range.setEnd(node, b + 1);
      const mark = doc.createElement('mark');
      mark.className = MARK_CLASS;
      mark.style.cssText = 'background:rgba(255,213,79,0.55);color:inherit;border-radius:2px;padding:0;';
      try {
        range.surroundContents(mark);
        marks.push(mark);
      } catch (err) { /* a range that cannot be wrapped is skipped */ }
    });

    if (marks.length) {
      console.log(`DocxHighlightViewer: matched text${ti + 1} -> ${marks.length} fragment(s)`);
      return { found: true, marks, matchedIndex: ti };
    }
  }

  console.log('DocxHighlightViewer: no match found across all text inputs');
  return { found: false, marks: [], matchedIndex: -1 };
};

/** Scrolls `scroller` (not the page or the modal around it) so `mark` sits about a third of the way down. */
const scrollToMark = (scroller, mark) => {
  if (!scroller || !mark) return;
  const sr = scroller.getBoundingClientRect();
  const mr = mark.getBoundingClientRect();
  const top = Math.max(0, scroller.scrollTop + (mr.top - sr.top) - scroller.clientHeight * 0.3);
  scroller.scrollTo({ top, behavior: 'smooth' });
};

// =====================================================================
//  COMPONENT
// =====================================================================

const bannerBase = { padding: '5px 12px', fontSize: '12px', lineHeight: 1.4, borderBottom: '1px solid #e0e4ea', flexShrink: 0 };

const DocxHighlightViewer = ({ docUrl, text1, text2, text3, fallback = null }) => {
  const hostRef = useRef(null);
  const scrollRef = useRef(null);
  const [phase, setPhase] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [match, setMatch] = useState('idle'); // 'idle' | 'found' | 'notfound'

  useEffect(() => {
    let cancelled = false;
    const timers = [];

    (async () => {
      try {
        const res = await fetch(docUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buffer = await res.arrayBuffer();
        if (cancelled || !hostRef.current) return;

        hostRef.current.innerHTML = '';
        await renderAsync(buffer, hostRef.current, null, { className: 'docx', inWrapper: true, breakPages: true });
        if (cancelled) return;
        setPhase('ready');

        const texts = [text1, text2, cleanChunk(text3)].filter(Boolean);
        if (texts.length === 0) return;

        const result = highlightPassage(hostRef.current, texts);
        setMatch(result.found ? 'found' : 'notfound');
        if (result.found) {
          // images and fonts settle after the first paint, so scroll again once the layout has stopped moving
          [120, 600].forEach((ms) => timers.push(setTimeout(() => scrollToMark(scrollRef.current, result.marks[0]), ms)));
        }
      } catch (err) {
        console.error('DocxHighlightViewer: could not load the document', err);
        if (!cancelled) setPhase('failed');
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [docUrl, text1, text2, text3]);

  if (phase === 'failed') {
    return fallback || (
      <div style={{ padding: 24, textAlign: 'center', color: '#b42318', fontSize: 13 }}>
        This Word file could not be opened here.
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {match === 'found' && <div style={{ ...bannerBase, background: '#fff8e1', color: '#7a5b00' }}>Highlighted passage.</div>}
      {match === 'notfound' && (
        <div style={{ ...bannerBase, background: '#fef3f2', color: '#b42318' }}>
          Could not locate this passage in the document, so nothing is highlighted.
        </div>
      )}
      {phase === 'loading' && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#475467' }}>Loading document...</div>}
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#e9ecef' }}>
        <div ref={hostRef} />
      </div>
    </div>
  );
};

export default DocxHighlightViewer;
